/**
 * CustomSelect - Generic dropdown component with Portal rendering
 *
 * Features:
 * - Portal rendering for proper z-index management in modals
 * - Smart positioning (auto-adjusts based on available space)
 * - Custom rendering support for options (badges, colors, etc.)
 * - Keyboard navigation and accessibility
 * - Click outside to close
 */

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { Z_DROPDOWN_MENU_TOP } from '../../lib/zIndex';
import { getPortalRoot } from '../../lib/portalUtils';

export interface SelectOption {
  value: string;
  label: string;
  color?: string;
  disabled?: boolean;
}

interface CustomSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  renderOption?: (option: SelectOption) => React.ReactNode;
  renderValue?: (option: SelectOption) => React.ReactNode;
}

export function CustomSelect({
  value,
  onChange,
  options,
  placeholder = 'Sélectionner...',
  disabled = false,
  className = '',
  renderOption,
  renderValue,
}: CustomSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(opt => opt.value === value);

  // Calculate dropdown position
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;
      const dropdownHeight = 300; // Max height estimate

      setDropdownPosition(spaceBelow < dropdownHeight && spaceAbove > spaceBelow ? 'top' : 'bottom');
    }
  }, [isOpen]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  const defaultRenderOption = (option: SelectOption) => (
    <span>{option.label}</span>
  );

  const defaultRenderValue = (option: SelectOption) => (
    <span>{option.label}</span>
  );

  const renderOptionContent = renderOption || defaultRenderOption;
  const renderValueContent = renderValue || defaultRenderValue;

  const dropdownContent = isOpen && triggerRef.current && (
    <div
      ref={dropdownRef}
      className="fixed bg-white border border-gray-300 rounded-lg shadow-lg max-h-[300px] overflow-y-auto"
      style={{
        zIndex: Z_DROPDOWN_MENU_TOP,
        left: `${triggerRef.current.getBoundingClientRect().left}px`,
        [dropdownPosition === 'bottom' ? 'top' : 'bottom']: dropdownPosition === 'bottom'
          ? `${triggerRef.current.getBoundingClientRect().bottom + 4}px`
          : `${window.innerHeight - triggerRef.current.getBoundingClientRect().top + 4}px`,
        width: `${triggerRef.current.offsetWidth}px`,
      }}
    >
      {options.length === 0 ? (
        <div className="px-3 py-2 text-sm text-gray-500">Aucune option disponible</div>
      ) : (
        options.map((option) => (
          <button
            key={option.value}
            type="button"
            disabled={option.disabled}
            onClick={() => !option.disabled && handleSelect(option.value)}
            className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 transition-colors ${
              option.value === value ? 'bg-primary-50 text-primary-600' : ''
            } ${option.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            {renderOptionContent(option)}
          </button>
        ))
      )}
    </div>
  );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-white hover:border-gray-400 transition-colors text-sm ${
          disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        } ${className}`}
      >
        <span className="flex-1 text-left">
          {selectedOption ? renderValueContent(selectedOption) : (
            <span className="text-gray-400">{placeholder}</span>
          )}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-gray-400 transition-transform ${
            isOpen ? 'transform rotate-180' : ''
          }`}
        />
      </button>
      {dropdownContent && createPortal(dropdownContent, getPortalRoot())}
    </>
  );
}
